<template>
  <div class="delivery-notes-overview">
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex justify-md-end align-center">
        <v-btn
          color="green"
          elevation="2"
          large
          style="color: white;"
          @click="openCreateDialog"
        >
          Neuen Lieferschein erstellen
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col cols="12" md="5" lg="4">
        <v-select
          v-model="selectedCustomerFilter"
          :items="customers"
          :item-text="customerLabel"
          item-value="id_customer"
          label="Nach Kunde filtern"
          clearable
          hide-details="auto"
        ></v-select>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredDeliveryNotes"
      :loading="isLoading"
      :footer-props="{ 'items-per-page-text': 'Einträge pro Seite:' }"
      class="elevation-0"
      sort-by="delivery_note_date"
      sort-desc
      no-data-text="Es wurden noch keine Lieferscheine erstellt."
    >
      <template v-slot:item.employee_name_snapshot="{ item }">
        <div class="delivery-notes-overview__employee-cell">
          <div class="delivery-notes-overview__employee-name">
            {{ employeeLastName(item) }}
          </div>
          <div class="delivery-notes-overview__employee-meta">
            {{ formatDate(item.created_at) }}
          </div>
        </div>
      </template>
      <template v-slot:item.source="{ item }">
        {{ item.source === "aufstockung" ? "Aufstockung" : "Manuell" }}
      </template>
      <template v-slot:item.employee_signed="{ item }">
        <div class="delivery-notes-overview__status-cell">
          <v-chip
            class="delivery-notes-overview__status-chip"
            :color="item.employee_signed_at ? 'green' : 'grey lighten-1'"
            dark
          >
            {{ item.employee_signed_at ? "Ja" : "Nein" }}
          </v-chip>
        </div>
      </template>
      <template v-slot:item.customer_signed="{ item }">
        <div class="delivery-notes-overview__status-cell">
          <v-chip
            class="delivery-notes-overview__status-chip"
            :color="item.customer_signed_at ? 'green' : 'grey lighten-1'"
            dark
          >
            {{ item.customer_signed_at ? "Ja" : "Nein" }}
          </v-chip>
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <div class="delivery-notes-overview__status-cell">
          <v-chip
            class="delivery-notes-overview__status-chip"
            :color="statusColor(item.status)"
            dark
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="delivery-notes-overview__action-buttons">
          <v-tooltip v-if="item.status === 'awaiting_customer'" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="delivery-notes-overview__table-action-button"
                aria-label="Versandstatus anzeigen"
                v-bind="attrs"
                v-on="on"
                @click.stop="openCustomerMailStatus(item)"
              >
                <v-icon>mdi-information-outline</v-icon>
              </v-btn>
            </template>
            <span>Versandstatus anzeigen</span>
          </v-tooltip>

          <v-menu
            offset-y
            left
            transition="slide-y-transition"
            content-class="delivery-notes-overview__action-menu-content"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="delivery-notes-overview__table-action-button"
                aria-label="Weitere Aktionen"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="delivery-notes-overview__action-menu">
              <v-list-item
                class="delivery-notes-overview__action-item"
                @click="openDetailDialog(item)"
              >
                <div class="delivery-notes-overview__action-row">
                  <span class="delivery-notes-overview__action-icon-wrap">
                    <v-icon class="delivery-notes-overview__action-icon">
                      mdi-eye-outline
                    </v-icon>
                  </span>
                  <span class="delivery-notes-overview__action-title">
                    Details
                  </span>
                </div>
              </v-list-item>
              <v-list-item
                class="delivery-notes-overview__action-item"
                :disabled="Boolean(item.employee_signed_at)"
                @click="openEmployeeSignDialog(item)"
              >
                <div class="delivery-notes-overview__action-row">
                  <span class="delivery-notes-overview__action-icon-wrap">
                    <v-icon class="delivery-notes-overview__action-icon">
                      mdi-pencil-outline
                    </v-icon>
                  </span>
                  <span class="delivery-notes-overview__action-title">
                    Intern unterschreiben
                  </span>
                </div>
              </v-list-item>
              <v-list-item
                class="delivery-notes-overview__action-item"
                @click="resend(item)"
              >
                <div class="delivery-notes-overview__action-row">
                  <span class="delivery-notes-overview__action-icon-wrap">
                    <v-icon class="delivery-notes-overview__action-icon">
                      mdi-email-outline
                    </v-icon>
                  </span>
                  <span class="delivery-notes-overview__action-content">
                    <span class="delivery-notes-overview__action-title">
                      Erneut senden
                    </span>
                    <span class="delivery-notes-overview__action-subtitle">
                      {{ resendTypeLabel(item) }}
                    </span>
                  </span>
                </div>
              </v-list-item>
              <v-list-item
                class="delivery-notes-overview__action-item"
                @click="downloadPdf(item)"
              >
                <div class="delivery-notes-overview__action-row">
                  <span class="delivery-notes-overview__action-icon-wrap">
                    <v-icon class="delivery-notes-overview__action-icon">
                      mdi-file-pdf-outline
                    </v-icon>
                  </span>
                  <span class="delivery-notes-overview__action-content">
                    <span class="delivery-notes-overview__action-title">
                      PDF
                    </span>
                    <span class="delivery-notes-overview__action-subtitle">
                      {{ pdfTypeLabel(item) }}
                    </span>
                  </span>
                </div>
              </v-list-item>
              <v-list-item
                class="delivery-notes-overview__action-item delivery-notes-overview__action-item--danger"
                :disabled="Boolean(item.customer_signed_at)"
                @click="deleteDeliveryNote(item)"
              >
                <div class="delivery-notes-overview__action-row">
                  <span class="delivery-notes-overview__action-icon-wrap">
                    <v-icon class="delivery-notes-overview__action-icon">
                      mdi-trash-can-outline
                    </v-icon>
                  </span>
                  <span class="delivery-notes-overview__action-content">
                    <span class="delivery-notes-overview__action-title">
                      Löschen
                    </span>
                    <span class="delivery-notes-overview__action-subtitle">
                      Nur bis zur Kundenunterschrift
                    </span>
                  </span>
                </div>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="createDialog" max-width="900px">
      <v-card>
        <v-card-title class="headline"
          >Neuen Lieferschein erstellen</v-card-title
        >
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="createForm.customer_id"
                :items="customers"
                :item-text="customerLabel"
                item-value="id_customer"
                label="Kunde auswählen"
                :disabled="isSubmitting"
                @change="handleCreateCustomerChange"
              ></v-select>
            </v-col>
          </v-row>

          <v-row v-if="createForm.customer_id">
            <v-col cols="12">
              <div
                v-if="isProductsLoading"
                class="delivery-notes-overview__hint"
              >
                Artikelliste wird geladen...
              </div>
              <div
                v-else-if="!availableProducts.length"
                class="delivery-notes-overview__hint"
              >
                Für diesen Kunden sind keine Artikel hinterlegt.
              </div>
            </v-col>
          </v-row>

          <div
            v-for="(item, index) in createForm.items"
            :key="`item-${index}`"
            class="delivery-notes-overview__item-row"
          >
            <v-row>
              <v-col cols="12" md="7">
                <v-autocomplete
                  v-model="item.product"
                  :items="availableProducts"
                  item-text="name"
                  return-object
                  label="Artikel auswählen"
                  :disabled="
                    isSubmitting ||
                      isProductsLoading ||
                      !createForm.customer_id ||
                      !availableProducts.length
                  "
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model.number="item.quantity"
                  label="Anzahl"
                  type="number"
                  min="1"
                  :disabled="isSubmitting"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn
                  text
                  color="red"
                  :disabled="createForm.items.length === 1 || isSubmitting"
                  @click="removeItemRow(index)"
                >
                  Entfernen
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <div class="delivery-notes-overview__add-item-wrap">
            <v-btn
              text
              color="green"
              class="delivery-notes-overview__add-item-btn"
              :disabled="isSubmitting"
              @click="addItemRow"
            >
              <v-icon left small>mdi-plus</v-icon>
              Position hinzufügen
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions class="delivery-notes-overview__dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text :disabled="isSubmitting" @click="createDialog = false">
            Abbrechen
          </v-btn>
          <v-btn
            color="green"
            elevation="2"
            style="color: white;"
            :loading="isSubmitting"
            @click="createDeliveryNote"
          >
            Erstellen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="760px">
      <v-card v-if="selectedNote">
        <v-card-title class="headline">
          Lieferschein {{ selectedNote.note_number }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <strong>Kunde:</strong> {{ selectedNote.customer_name_snapshot }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Erstellt von:</strong>
              {{ employeeLastName(selectedNote) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Lieferscheindatum:</strong>
              {{ formatDateOnly(selectedNote.delivery_note_date) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Mitarbeiter unterschrieben:</strong>
              {{
                selectedNote.employee_signed_at
                  ? formatDate(selectedNote.employee_signed_at)
                  : "Nein"
              }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Kunde unterschrieben:</strong>
              {{
                selectedNote.customer_signed_at
                  ? formatDate(selectedNote.customer_signed_at)
                  : "Nein"
              }}
            </v-col>
          </v-row>
          <v-simple-table class="mt-4">
            <thead>
              <tr>
                <th>Art.-Nr.</th>
                <th>Artikel</th>
                <th>Anzahl</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedNote.items" :key="`detail-${item.id}`">
                <td>{{ item.external_item_id || "-" }}</td>
                <td>{{ item.item_name }}</td>
                <td>{{ item.quantity }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="customerMailStatusDialog" max-width="680px">
      <v-card class="delivery-notes-overview__mail-dialog">
        <v-card-title class="delivery-notes-overview__mail-dialog-header">
          <span class="delivery-notes-overview__mail-dialog-icon">
            <v-icon>mdi-email-outline</v-icon>
          </span>
          <span>
            <span class="delivery-notes-overview__mail-dialog-title">
              Versandstatus Kundenunterschrift
            </span>
            <span
              v-if="customerMailStatusNote"
              class="delivery-notes-overview__mail-dialog-subtitle"
            >
              Lieferschein {{ customerMailStatusNote.note_number }}
            </span>
          </span>
        </v-card-title>

        <v-card-text class="delivery-notes-overview__mail-dialog-body">
          <div
            v-if="customerMailStatusLoading"
            class="delivery-notes-overview__mail-dialog-loading"
          >
            <v-progress-circular
              indeterminate
              color="green"
              size="30"
              width="3"
            ></v-progress-circular>
            <span>Versandinformationen werden geladen...</span>
          </div>

          <div
            v-else-if="customerMailStatusError"
            class="delivery-notes-overview__mail-dialog-error"
          >
            <v-icon>mdi-alert-circle-outline</v-icon>
            <span>{{ customerMailStatusError }}</span>
          </div>

          <template v-else-if="customerMailStatus">
            <div class="delivery-notes-overview__mail-recipient">
              <span class="delivery-notes-overview__mail-recipient-icon">
                <v-icon>mdi-email-outline</v-icon>
              </span>
              <span>
                <span class="delivery-notes-overview__mail-meta-label">
                  Empfänger der Signatur-E-Mail
                </span>
                <strong>{{ customerMailStatus.customer_email || "-" }}</strong>
              </span>
            </div>

            <div class="delivery-notes-overview__mail-status-grid">
              <div class="delivery-notes-overview__mail-status-card">
                <div class="delivery-notes-overview__mail-status-card-header">
                  <span>Signaturanfrage</span>
                  <v-chip
                    x-small
                    dark
                    :color="
                      mailStatusColor(customerMailStatus.invitation.status)
                    "
                  >
                    {{ mailStatusLabel(customerMailStatus.invitation.status) }}
                  </v-chip>
                </div>
                <span class="delivery-notes-overview__mail-meta-label">
                  {{ invitationDateLabel(customerMailStatus.invitation) }}
                </span>
                <strong>
                  {{ invitationDateValue(customerMailStatus.invitation) }}
                </strong>
              </div>

              <div class="delivery-notes-overview__mail-status-card">
                <div class="delivery-notes-overview__mail-status-card-header">
                  <span>Letzte Erinnerung</span>
                </div>
                <span class="delivery-notes-overview__mail-meta-label">
                  Zuletzt versendet
                </span>
                <strong>
                  {{
                    customerMailStatus.reminder.last_sent_at
                      ? formatDateLong(customerMailStatus.reminder.last_sent_at)
                      : "Noch keine Erinnerung"
                  }}
                </strong>
              </div>

              <div class="delivery-notes-overview__mail-status-card">
                <div class="delivery-notes-overview__mail-status-card-header">
                  <span>Nächste Erinnerung</span>
                </div>
                <span class="delivery-notes-overview__mail-meta-label">
                  Geplanter Zeitpunkt
                </span>
                <strong>
                  {{
                    customerMailStatus.reminder.next_at
                      ? formatDateLong(customerMailStatus.reminder.next_at)
                      : nextReminderFallback(customerMailStatus)
                  }}
                </strong>
              </div>
            </div>
          </template>
        </v-card-text>

        <v-card-actions class="delivery-notes-overview__dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="customerMailStatusDialog = false">
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="employeeSignDialog" max-width="760px">
      <v-card>
        <v-card-title class="headline">Mitarbeiter-Unterschrift</v-card-title>
        <v-card-text>
          <div class="delivery-notes-overview__signature-wrap">
            <VueSignaturePad
              v-if="employeeSignDialog"
              ref="employeeSignaturePad"
              :key="selectedNote ? selectedNote.id : 'employee-signature-pad'"
              width="100%"
              height="220px"
            />
          </div>
          <div class="mt-4">
            Nach der internen Unterschrift wird die Signaturanfrage für den
            Kunden eingeplant.
          </div>
        </v-card-text>
        <v-card-actions class="delivery-notes-overview__dialog-actions">
          <v-btn text @click="clearEmployeeSignature">Zurücksetzen</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="isSubmitting"
            @click="employeeSignDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="green"
            elevation="2"
            style="color: white;"
            :loading="isSubmitting"
            @click="submitEmployeeSignature"
          >
            Unterschrift speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";
import moment from "moment";

export default {
  name: "DeliveryNotesOverview",
  props: {
    customers: {
      type: Array,
      default() {
        return [];
      }
    }
  },
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      createDialog: false,
      detailDialog: false,
      employeeSignDialog: false,
      customerMailStatusDialog: false,
      customerMailStatusLoading: false,
      customerMailStatusError: "",
      customerMailStatus: null,
      customerMailStatusNote: null,
      isProductsLoading: false,
      selectedCustomerFilter: null,
      selectedNote: null,
      deliveryNotes: [],
      availableProducts: [],
      createForm: {
        customer_id: null,
        items: [
          {
            product: null,
            quantity: 1
          }
        ]
      },
      headers: [
        { text: "Nr.", value: "note_number" },
        { text: "Kunde", value: "customer_name_snapshot" },
        { text: "Erstellt von", value: "employee_name_snapshot" },
        { text: "Quelle", value: "source" },
        {
          text: "Mitarbeiter",
          value: "employee_signed",
          sortable: false,
          align: "center"
        },
        {
          text: "Kunde",
          value: "customer_signed",
          sortable: false,
          align: "center"
        },
        { text: "Status", value: "status", align: "center" },
        { text: "Aktionen", value: "actions", sortable: false }
      ]
    };
  },
  computed: {
    filteredDeliveryNotes() {
      if (!this.selectedCustomerFilter) {
        return this.deliveryNotes;
      }

      return this.deliveryNotes.filter(note => {
        return Number(note.customer_id) === Number(this.selectedCustomerFilter);
      });
    }
  },
  created() {
    this.fetchDeliveryNotes();
  },
  methods: {
    getEmployeeSignaturePad() {
      const ref = this.$refs.employeeSignaturePad;

      if (Array.isArray(ref)) {
        return ref[0] || null;
      }

      return ref || null;
    },
    fetchDeliveryNotes() {
      this.isLoading = true;
      ApiService.query("delivery-notes")
        .then(({ data }) => {
          this.deliveryNotes = data.delivery_notes || [];
        })
        .catch(() => {
          Swal.fire({
            title: "Fehler",
            text: "Die Lieferscheine konnten nicht geladen werden.",
            icon: "error",
            confirmButtonText: "Ok"
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    customerLabel(customer) {
      return `${customer.company} ${
        customer.company2 ? `(${customer.company2})` : ""
      }`;
    },
    statusLabel(status) {
      if (status === "draft") return "Wartet auf Mitarbeiter";
      if (status === "awaiting_customer") return "Wartet auf Kunde";
      if (status === "completed") return "Abgeschlossen";
      if (status === "cancelled") return "Abgebrochen";
      return status;
    },
    statusColor(status) {
      if (status === "awaiting_customer") return "orange darken-2";
      if (status === "completed") return "green";
      if (status === "cancelled") return "red";
      return "blue-grey";
    },
    resendTypeLabel(note) {
      if (note.status === "draft") return "Interne Info-E-Mail";
      if (note.status === "completed") return "Abschluss-E-Mail";
      return "Signatur-E-Mail";
    },
    pdfTypeLabel(note) {
      return note.status === "completed"
        ? "Mit Unterschriften"
        : "Ohne Unterschriften";
    },
    employeeLastName(note) {
      const displayName =
        note.employee_display_name || note.employee_name_snapshot || "";
      const nameParts = displayName
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      return nameParts.length ? nameParts[nameParts.length - 1] : "-";
    },
    formatDate(value) {
      if (!value) return "-";
      return moment(String(value)).format("DD.MM.YY HH:mm");
    },
    formatDateOnly(value) {
      if (!value) return "-";
      return moment(String(value)).format("DD.MM.YY");
    },
    formatDateLong(value) {
      if (!value) return "-";
      return moment(String(value)).format("DD.MM.YYYY [um] HH:mm [Uhr]");
    },
    mailStatusLabel(status) {
      if (status === "queued") return "Geplant";
      if (status === "sending") return "Wird versendet";
      if (status === "sent") return "Versendet";
      if (status === "failed") return "Fehlgeschlagen";
      if (status === "cancelled") return "Storniert";
      return "Nicht eingeplant";
    },
    mailStatusColor(status) {
      if (status === "sent") return "green";
      if (status === "failed") return "red darken-1";
      if (status === "cancelled") return "grey";
      return "orange darken-2";
    },
    invitationDateLabel(invitation) {
      if (invitation.status === "sent" && invitation.sent_at) {
        return "Versendet am";
      }
      if (invitation.status === "failed" && invitation.failed_at) {
        return "Fehlgeschlagen am";
      }
      if (invitation.scheduled_at) {
        return "Geplant für";
      }
      return "Versandzeitpunkt";
    },
    invitationDateValue(invitation) {
      if (invitation.status === "sent" && invitation.sent_at) {
        return this.formatDateLong(invitation.sent_at);
      }
      if (invitation.status === "failed" && invitation.failed_at) {
        return this.formatDateLong(invitation.failed_at);
      }
      if (invitation.scheduled_at) {
        return this.formatDateLong(invitation.scheduled_at);
      }
      return "Noch nicht festgelegt";
    },
    nextReminderFallback(mailStatus) {
      if (mailStatus.delivery_note_status === "draft") {
        return "Nach der Mitarbeiter-Unterschrift";
      }
      if (
        mailStatus.delivery_note_status === "completed" ||
        mailStatus.delivery_note_status === "cancelled"
      ) {
        return "Nicht mehr erforderlich";
      }
      return "Nach Versand der Signaturanfrage";
    },
    openCustomerMailStatus(note) {
      this.customerMailStatusNote = note;
      this.customerMailStatus = null;
      this.customerMailStatusError = "";
      this.customerMailStatusLoading = true;
      this.customerMailStatusDialog = true;

      ApiService.get("delivery-notes", `${note.id}/customer-mail-status`)
        .then(({ data }) => {
          this.customerMailStatus = data.mail_status || null;
          if (!this.customerMailStatus) {
            this.customerMailStatusError =
              "Für diesen Lieferschein sind keine Versandinformationen verfügbar.";
          }
        })
        .catch(() => {
          this.customerMailStatusError =
            "Die Versandinformationen konnten nicht geladen werden.";
        })
        .finally(() => {
          this.customerMailStatusLoading = false;
        });
    },
    resetCreateForm() {
      this.createForm = {
        customer_id: null,
        items: [
          {
            product: null,
            quantity: 1
          }
        ]
      };
      this.availableProducts = [];
      this.isProductsLoading = false;
    },
    openCreateDialog() {
      this.resetCreateForm();
      this.createDialog = true;
    },
    handleCreateCustomerChange(customerId) {
      this.createForm.items = [
        {
          product: null,
          quantity: 1
        }
      ];
      this.availableProducts = [];

      if (!customerId) {
        return;
      }

      this.isProductsLoading = true;
      ApiService.get("protokoll/product", customerId)
        .then(response => {
          if (response.data.products && response.data.products.length) {
            this.availableProducts = response.data.products;
            return;
          }

          Swal.fire({
            title: "Info - Keine Artikel gefunden",
            text:
              response.data.message ||
              "Für diesen Kunden konnten keine Artikel geladen werden.",
            icon: "info",
            confirmButtonText: "Ok"
          });
        })
        .catch(() => {
          Swal.fire({
            title: "Fehler",
            text: "Die Artikelliste konnte nicht geladen werden.",
            icon: "error",
            confirmButtonText: "Ok"
          });
        })
        .finally(() => {
          this.isProductsLoading = false;
        });
    },
    addItemRow() {
      this.createForm.items.push({
        product: null,
        quantity: 1
      });
    },
    removeItemRow(index) {
      this.createForm.items.splice(index, 1);
    },
    resolveInternalItemId(product) {
      const candidates = [
        product?.internal_item_id,
        product?.item_id,
        product?.id
      ];

      for (const candidate of candidates) {
        const parsed = Number(candidate);
        if (Number.isInteger(parsed) && parsed > 0) {
          return parsed;
        }
      }

      return null;
    },
    resolveExternalItemId(product) {
      if (
        product?.external_item_id === null ||
        product?.external_item_id === undefined
      ) {
        return null;
      }

      const externalItemId = String(product.external_item_id).trim();

      return externalItemId || null;
    },
    getApiErrorMessage(error, fallbackMessage) {
      const validationErrors = error?.response?.data?.errors;
      if (validationErrors) {
        const firstError = Object.values(validationErrors)
          .flat()
          .find(Boolean);

        if (firstError) {
          return firstError;
        }
      }

      return error?.response?.data?.message || fallbackMessage;
    },
    createDeliveryNote() {
      const cleanedItems = this.createForm.items
        .map(item => ({
          item_name: item.product?.name ? item.product.name.trim() : "",
          internal_item_id: this.resolveInternalItemId(item.product),
          external_item_id: this.resolveExternalItemId(item.product),
          quantity: Number(item.quantity)
        }))
        .filter(item => item.item_name && item.quantity > 0);

      if (
        !this.createForm.customer_id ||
        !this.availableProducts.length ||
        !cleanedItems.length
      ) {
        Swal.fire({
          title: "Hinweis",
          text:
            "Bitte Kunde und mindestens eine gültige Artikelposition angeben.",
          icon: "warning",
          confirmButtonText: "Ok"
        });
        return;
      }

      this.isSubmitting = true;
      ApiService.post("delivery-notes", {
        customer_id: this.createForm.customer_id,
        items: cleanedItems
      })
        .then(() => {
          this.createDialog = false;
          this.fetchDeliveryNotes();
          Swal.fire({
            title: "Lieferschein erstellt",
            text:
              "Der Lieferschein wurde erfolgreich erstellt. Die interne Benachrichtigung wird im Hintergrund verarbeitet.",
            icon: "success",
            confirmButtonText: "Ok"
          });
        })
        .catch(error => {
          const message = this.getApiErrorMessage(
            error,
            "Der Lieferschein konnte nicht erstellt werden."
          );
          Swal.fire({
            title: "Fehler",
            text: message,
            icon: "error",
            confirmButtonText: "Ok"
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    openDetailDialog(note) {
      this.selectedNote = note;
      this.detailDialog = true;
    },
    openEmployeeSignDialog(note) {
      this.selectedNote = note;
      this.employeeSignDialog = true;
      this.prepareEmployeeSignaturePad();
    },
    prepareEmployeeSignaturePad() {
      this.$nextTick(() => {
        window.setTimeout(() => {
          const signaturePad = this.getEmployeeSignaturePad();
          if (!signaturePad) {
            return;
          }

          signaturePad.resizeCanvas();
          signaturePad.clearSignature();
          signaturePad.openSignaturePad();
        }, 75);
      });
    },
    clearEmployeeSignature() {
      const signaturePad = this.getEmployeeSignaturePad();
      if (signaturePad) {
        signaturePad.clearSignature();
      }
    },
    submitEmployeeSignature() {
      if (!this.selectedNote) return;

      const signaturePad = this.getEmployeeSignaturePad();
      if (!signaturePad) {
        Swal.fire({
          title: "Fehler",
          text: "Das Signaturfeld konnte nicht geladen werden.",
          icon: "error",
          confirmButtonText: "Ok"
        });
        return;
      }

      const { isEmpty, data } = signaturePad.saveSignature();
      if (isEmpty) {
        Swal.fire({
          title: "Hinweis",
          text: "Bitte zuerst unterschreiben.",
          icon: "warning",
          confirmButtonText: "Ok"
        });
        return;
      }

      this.isSubmitting = true;
      ApiService.post(`delivery-notes/${this.selectedNote.id}/employee-sign`, {
        signature: data
      })
        .then(() => {
          this.employeeSignDialog = false;
          this.fetchDeliveryNotes();
          Swal.fire({
            title: "Gespeichert",
            text:
              "Die Mitarbeiter-Unterschrift wurde erfolgreich übermittelt. Die Benachrichtigung wird im Hintergrund verarbeitet.",
            icon: "success",
            confirmButtonText: "Ok"
          });
        })
        .catch(error => {
          const message =
            error?.response?.data?.message ||
            "Die Mitarbeiter-Unterschrift konnte nicht gespeichert werden.";
          Swal.fire({
            title: "Fehler",
            text: message,
            icon: "error",
            confirmButtonText: "Ok"
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    resend(note) {
      const confirmationText =
        note.status === "draft"
          ? "Die interne Info-E-Mail wird erneut an Mitarbeiter und Buchhaltung versendet."
          : note.status === "completed"
          ? "Die Abschluss-E-Mail wird erneut an alle Beteiligten versendet."
          : "Die Signatur-E-Mail wird erneut ausschließlich an den Kunden versendet.";

      Swal.fire({
        title: "E-Mail erneut senden?",
        text: confirmationText,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Senden",
        cancelButtonText: "Abbrechen",
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: () => {
          return ApiService.post(`delivery-notes/${note.id}/resend`, {})
            .then(() => {
              this.fetchDeliveryNotes();
            })
            .catch(error => {
              const message = this.getApiErrorMessage(
                error,
                "Die E-Mail konnte nicht erneut versendet werden."
              );

              Swal.showValidationMessage(message);
              throw error;
            });
        }
      }).then(result => {
        if (!result.isConfirmed) {
          return;
        }

        Swal.fire({
          title: "Erfolgreich übermittelt",
          text:
            "Der erneute Versand wurde erfolgreich übermittelt und wird nun verarbeitet.",
          icon: "success",
          confirmButtonText: "Ok"
        });
      });
    },
    deleteDeliveryNote(note) {
      Swal.fire({
        title: "Lieferschein löschen?",
        text:
          "Der Lieferschein wird dauerhaft gelöscht. Ein vorhandener Signaturlink ist danach nicht mehr aufrufbar.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Löschen",
        cancelButtonText: "Abbrechen",
        confirmButtonColor: "#d64545",
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: () => {
          return ApiService.delete(`delivery-notes/${note.id}`).catch(error => {
            const message = this.getApiErrorMessage(
              error,
              "Der Lieferschein konnte nicht gelöscht werden."
            );

            Swal.showValidationMessage(message);
            throw error;
          });
        }
      }).then(result => {
        if (!result.isConfirmed) {
          return;
        }

        this.fetchDeliveryNotes();
        Swal.fire({
          title: "Lieferschein gelöscht",
          text: "Der Lieferschein wurde erfolgreich gelöscht.",
          icon: "success",
          confirmButtonText: "Ok"
        });
      });
    },
    downloadPdf(note) {
      ApiService.getFile(`delivery-notes/${note.id}`, "pdf")
        .then(response => {
          const blob = new Blob([response.data], {
            type: "application/pdf"
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `LS_${note.note_number}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          Swal.fire({
            title: "Fehler",
            text: "Das PDF konnte nicht geladen werden.",
            icon: "error",
            confirmButtonText: "Ok"
          });
        });
    }
  }
};
</script>

<style scoped>
.delivery-notes-overview__title {
  margin-bottom: 8px;
  color: #2d3443;
  font-size: 28px;
  line-height: 1.2;
}

.delivery-notes-overview__text {
  margin: 0;
  color: #7b8190;
  font-size: 15px;
  line-height: 1.6;
}

.delivery-notes-overview__hint {
  color: #7b8190;
  font-size: 14px;
}

.delivery-notes-overview__employee-cell {
  line-height: 1.35;
}

.delivery-notes-overview__employee-name {
  color: #2d3443;
  font-weight: 600;
}

.delivery-notes-overview__employee-meta {
  margin-top: 3px;
  color: #8c95a3;
  font-size: 12px;
}

.delivery-notes-overview__status-chip {
  min-height: 28px !important;
  padding: 0 10px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0.01em !important;
}

.delivery-notes-overview__status-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.delivery-notes-overview__action-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.delivery-notes-overview__table-action-button {
  color: #657287 !important;
}

.delivery-notes-overview__table-action-button .v-icon {
  font-size: 20px !important;
}

.delivery-notes-overview__mail-dialog {
  overflow: hidden;
  border-radius: 16px !important;
}

.delivery-notes-overview__mail-dialog-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 18px !important;
  border-bottom: 1px solid #edf1f5;
}

.delivery-notes-overview__mail-dialog-icon,
.delivery-notes-overview__mail-recipient-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border-radius: 12px;
  background: #e9f6ed;
  color: #167337;
}

.delivery-notes-overview__mail-dialog-icon {
  width: 44px;
  height: 44px;
}

.delivery-notes-overview__mail-dialog-icon .v-icon,
.delivery-notes-overview__mail-recipient-icon .v-icon {
  color: inherit !important;
}

.delivery-notes-overview__mail-dialog-title,
.delivery-notes-overview__mail-dialog-subtitle,
.delivery-notes-overview__mail-meta-label {
  display: block;
}

.delivery-notes-overview__mail-dialog-title {
  color: #2d3443;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.25;
}

.delivery-notes-overview__mail-dialog-subtitle {
  margin-top: 3px;
  color: #8c95a3;
  font-size: 13px;
  font-weight: 500;
}

.delivery-notes-overview__mail-dialog-body {
  padding: 22px 24px 24px !important;
}

.delivery-notes-overview__mail-dialog-loading,
.delivery-notes-overview__mail-dialog-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 170px;
  color: #6f7887;
  text-align: center;
}

.delivery-notes-overview__mail-dialog-error .v-icon {
  color: #c44949 !important;
}

.delivery-notes-overview__mail-recipient {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #dcece2;
  border-radius: 12px;
  background: #f7fbf8;
  color: #2d3443;
  overflow-wrap: anywhere;
}

.delivery-notes-overview__mail-recipient-icon {
  width: 36px;
  height: 36px;
}

.delivery-notes-overview__mail-meta-label {
  margin-bottom: 4px;
  color: #8c95a3;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.delivery-notes-overview__mail-status-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.delivery-notes-overview__mail-status-card {
  min-width: 0;
  padding: 15px;
  border: 1px solid #e4eaf1;
  border-radius: 12px;
  background: #ffffff;
}

.delivery-notes-overview__mail-status-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 24px;
  margin-bottom: 14px;
  color: #2d3443;
  font-size: 13px;
  font-weight: 700;
}

.delivery-notes-overview__mail-status-card strong {
  display: block;
  color: #2d3443;
  font-size: 13px;
  line-height: 1.4;
}

@media (max-width: 767px) {
  .delivery-notes-overview__mail-status-grid {
    grid-template-columns: 1fr;
  }

  .delivery-notes-overview__mail-dialog-header,
  .delivery-notes-overview__mail-dialog-body {
    padding-right: 18px !important;
    padding-left: 18px !important;
  }
}

.delivery-notes-overview__action-menu {
  min-width: 262px;
  padding: 6px !important;
  background: #ffffff !important;
  border: 1px solid #e4eaf1;
  border-radius: 14px !important;
  box-shadow: 0 14px 30px rgba(20, 35, 54, 0.11) !important;
  overflow: hidden;
}

.delivery-notes-overview__action-item {
  min-height: 46px !important;
  padding: 0 6px !important;
  border-radius: 10px !important;
}

.delivery-notes-overview__action-item:not(.v-list-item--disabled):hover {
  background: #f5f8fb;
}

.delivery-notes-overview__action-item.v-list-item--disabled {
  opacity: 1 !important;
}

.delivery-notes-overview__action-item.v-list-item--disabled
  .delivery-notes-overview__action-icon-wrap {
  background: #f3f5f8;
}

.delivery-notes-overview__action-item.v-list-item--disabled
  .delivery-notes-overview__action-icon {
  color: #aab3c0 !important;
}

.delivery-notes-overview__action-item.v-list-item--disabled
  .delivery-notes-overview__action-title,
.delivery-notes-overview__action-item.v-list-item--disabled
  .delivery-notes-overview__action-subtitle {
  color: #aab3c0 !important;
}

.delivery-notes-overview__action-item--danger:not(.v-list-item--disabled):hover {
  background: #fff3f3;
}

.delivery-notes-overview__action-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.delivery-notes-overview__action-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.delivery-notes-overview__action-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 34px !important;
  width: 34px;
  height: 34px;
  margin: 0 10px 0 0 !important;
  border-radius: 10px;
  background: #f3f6fa;
  flex: 0 0 34px;
}

.delivery-notes-overview__action-icon {
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #57667b !important;
  font-size: 18px !important;
  line-height: 1 !important;
}

.delivery-notes-overview__action-title {
  display: block;
  line-height: 1.25;
  color: #2d3443;
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  white-space: nowrap;
}

.delivery-notes-overview__action-subtitle {
  margin-top: 2px;
  color: #8c95a3;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
}

.delivery-notes-overview__item-row {
  border-bottom: 1px solid #edf1f5;
}

.delivery-notes-overview__add-item-wrap {
  display: flex;
  justify-content: center;
  margin-top: 18px;
}

.delivery-notes-overview__add-item-btn {
  font-weight: 600;
  letter-spacing: 0 !important;
}

.delivery-notes-overview__dialog-actions {
  padding: 0 24px 24px !important;
}

.delivery-notes-overview__signature-wrap {
  border: 1px solid #dfe5ef;
  border-radius: 8px;
  overflow: hidden;
  min-height: 220px;
  background: #ffffff;
}
</style>

<style>
.delivery-notes-overview__action-menu-content {
  padding: 0 !important;
  background: transparent !important;
  border-radius: 14px !important;
  box-shadow: none !important;
  overflow: hidden !important;
}

.delivery-notes-overview__action-menu-content .v-list {
  background: #ffffff !important;
}
</style>
